import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, RefreshCw, FileText, Eye, X, Send } from 'lucide-react';

const ItemRequests = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  
  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [requestDetails, setRequestDetails] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // View dialog
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'PRODUCTION', 'RM_STORE']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [requestsRes, employeesRes, itemsRes] = await Promise.all([
        api.get('/item-requests'),
        api.get('/employees'),
        api.get('/items')
      ]);
      setData(requestsRes.data);
      setEmployees(employeesRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getEmployeeName = (employeeId) => {
    if (!employeeId) return '-';
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.employee_code} - ${emp.first_name} ${emp.last_name || ''}` : `ID: ${employeeId}`;
  };

  const getItemName = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? `${item.code} - ${item.name}` : `ID: ${itemId}`;
  };

  const filteredItems = items.filter(i => {
    if (!itemSearch) return i.is_active;
    const term = itemSearch.toLowerCase();
    return i.is_active && (i.code.toLowerCase().includes(term) || i.name.toLowerCase().includes(term));
  });

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        request_date: item.request_date || '',
        from_employee_id: (item.from_employee_id ? item.from_employee_id.toString() : ''),
        remarks: item.remarks || '',
      });
      setRequestDetails((item.details || []).map(d => ({
        item_id: (d.item_id ? d.item_id.toString() : ''),
        quantity: d.quantity || '',
      })) || []);
    } else {
      setEditingItem(null);
      setFormData({
        request_date: new Date().toISOString().split('T')[0],
        from_employee_id: '',
        remarks: '',
      });
      setRequestDetails([]);
    }
    setItemSearch('');
    setIsDialogOpen(true);
  };

  const handleAddItem = () => {
    setRequestDetails([...requestDetails, { item_id: '', quantity: '' }]);
  };

  const handleDetailChange = (index, field, value) => {
    const updated = [...requestDetails];
    updated[index] = { ...updated[index], [field]: value };
    setRequestDetails(updated);
  };

  const handleRemoveItem = (index) => {
    setRequestDetails(requestDetails.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.from_employee_id || !formData.request_date) {
      toast.error('Employee and Date are required');
      return;
    }
    if (requestDetails.length === 0 || !requestDetails.some(d => d.item_id && d.quantity)) {
      toast.error('At least one item is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        request_date: formData.request_date,
        from_employee_id: parseInt(formData.from_employee_id),
        remarks: formData.remarks || null,
        details: requestDetails.filter(d => d.item_id && d.quantity).map(d => ({
          item_id: parseInt(d.item_id),
          quantity: parseFloat(d.quantity),
        })),
      };

      if (editingItem) {
        await api.put(`/item-requests/${editingItem.id}`, {
          request_date: formData.request_date,
          from_employee_id: parseInt(formData.from_employee_id),
          remarks: formData.remarks || null,
        });
        toast.success('Request updated');
      } else {
        await api.post('/item-requests', submitData);
        toast.success('Request created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    try {
      await api.delete(`/item-requests/${item.id}`);
      toast.success('Request deleted');
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Delete failed');
    }
  };

  const handleView = async (item) => {
    try {
      const response = await api.get(`/item-requests/${item.id}`);
      setViewingRequest(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load request details');
    }
  };

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const empName = getEmployeeName(item.from_employee_id).toLowerCase();
    return empName.includes(term) || 
           (item.request_no ? item.request_no.toString() : '').includes(term) ||
           (item.status || '').toLowerCase().includes(term);
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>;
      case 'PARTIALLY_ISSUED':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Partially Issued</span>;
      case 'ISSUED':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Issued</span>;
      case 'CANCELLED':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Cancelled</span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="item-requests">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <FileText className="w-6 h-6 text-sky-500" />
            Item Requests
          </h1>
          <p className="text-slate-500 text-sm">{data.length} records</p>
        </div>
        {canEdit && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="add-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Request
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by Request No, Employee, Status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No requests found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Request No</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>From Employee</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono font-bold">REQ-{item.request_no}</TableCell>
                      <TableCell>{new Date(item.request_date).toLocaleDateString()}</TableCell>
                      <TableCell>{getEmployeeName(item.from_employee_id)}</TableCell>
                      <TableCell>{(item.details ? item.details.length : 0) || 0} items</TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleView(item)} className="h-8 w-8">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEdit && item.status === 'PENDING' && (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)} className="h-8 w-8">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} className="h-8 w-8 text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Request Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? `Edit Request - REQ-${editingItem.request_no}` : 'New Item Request'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div>
                <Label className="form-label">Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={formData.request_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, request_date: e.target.value }))}
                  required
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">From Employee <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.from_employee_id}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, from_employee_id: val }))}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select Employee" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {employees.filter(e => e.is_active).map((emp) => (
                      <SelectItem key={emp.id} value={emp.id.toString()}>
                        {emp.employee_code} - {emp.first_name} {emp.last_name || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Request Items Section */}
            <div className="mt-4 border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Request Items</h3>
                {!editingItem && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddItem}
                    className="rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </Button>
                )}
              </div>
              
              {requestDetails.length === 0 ? (
                <div className="text-center py-6 text-slate-500 border border-dashed rounded-sm">
                  No items added. Click "Add Item" to add request items.
                </div>
              ) : (
                <div className="space-y-3">
                  {requestDetails.map((detail, index) => (
                    <div key={index} className="flex items-end gap-3 p-3 bg-slate-50 rounded-sm">
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500">Item</Label>
                        <div className="space-y-1">
                          <Input
                            placeholder="Search item..."
                            value={itemSearch}
                            onChange={(e) => setItemSearch(e.target.value)}
                            className="rounded-sm text-sm"
                            disabled={!!editingItem}
                          />
                          <Select
                            value={detail.item_id}
                            onValueChange={(val) => handleDetailChange(index, 'item_id', val)}
                            disabled={!!editingItem}
                          >
                            <SelectTrigger className="rounded-sm">
                              <SelectValue placeholder="Select Item" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {filteredItems.slice(0, 50).map((item) => (
                                <SelectItem key={item.id} value={item.id.toString()}>
                                  {item.code} - {item.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="w-32">
                        <Label className="text-xs text-slate-500">Quantity</Label>
                        <Input
                          type="number"
                          step="0.0001"
                          value={detail.quantity}
                          onChange={(e) => handleDetailChange(index, 'quantity', e.target.value)}
                          placeholder="0"
                          className="rounded-sm"
                          disabled={!!editingItem}
                        />
                      </div>
                      {!editingItem && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4">
              <Label className="form-label">Remarks</Label>
              <Input
                value={formData.remarks}
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Any additional notes..."
                className="rounded-sm"
              />
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create Request'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Request Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Details - REQ-{(viewingRequest && viewingRequest.request_no)}</DialogTitle>
          </DialogHeader>
          {viewingRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <span className="text-sm text-slate-500">Date</span>
                  <p className="font-medium">{new Date(viewingRequest.request_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">From Employee</span>
                  <p className="font-medium">{getEmployeeName(viewingRequest.from_employee_id)}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Status</span>
                  <p>{getStatusBadge(viewingRequest.status)}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Requested Items ({(viewingRequest.details ? viewingRequest.details.length : 0) || 0})</h4>
                {(viewingRequest.details ? viewingRequest.details.length : 0) > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Item</TableHead>
                        <TableHead>Requested</TableHead>
                        <TableHead>Issued</TableHead>
                        <TableHead>Pending</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewingRequest.details.map((detail, index) => {
                        const requested = parseFloat(detail.quantity) || 0;
                        const issued = parseFloat(detail.issued_quantity) || 0;
                        const pending = Math.max(0, requested - issued);
                        return (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{getItemName(detail.item_id)}</TableCell>
                            <TableCell className="font-mono">{requested.toFixed(2)}</TableCell>
                            <TableCell className="font-mono text-green-600">{issued.toFixed(2)}</TableCell>
                            <TableCell className="font-mono text-orange-600">{pending.toFixed(2)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-slate-500">No items in request</p>
                )}
              </div>

              {viewingRequest.remarks && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Remarks</h4>
                  <p className="text-slate-600">{viewingRequest.remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ItemRequests;
